import { useState, useEffect, useMemo } from 'react';
import type { MockInspection, MockInspectionResult } from '@/mocks/qms';
import { getAllInspectionChecklists } from '@/api/inspectionchecklist.api';
import { getAllQualityParameters } from '@/api/qualityparameter.api';
import { qcService } from '@/services/qcService';

interface Props {
  inspection: MockInspection | null;
  results: MockInspectionResult[];
  onClose: () => void;
}

interface ChecklistState {
  id: string;
  name: string;
  parameters: string[];
}

interface QualityParameterState {
  id: string;
  name: string;
  code: string;
  type: string;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
}

export default function ViewInspectionModal({ inspection, results, onClose }: Props) {
  const [checklists, setChecklists] = useState<ChecklistState[]>([]);
  const [qualityParameters, setQualityParameters] = useState<QualityParameterState[]>([]);
  const [loadedResults, setLoadedResults] = useState<MockInspectionResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [checklistsRes, qpRes] = await Promise.all([
          getAllInspectionChecklists(),
          getAllQualityParameters(),
        ]);
        if (!active) return;

        if (checklistsRes.success && checklistsRes.data) {
          const mappedChecklists: ChecklistState[] = checklistsRes.data.map((c) => ({
            id: c.id,
            name: c.name,
            parameters: c.parameter_ids || [],
          }));
          setChecklists(mappedChecklists);
        }

        if (qpRes.success && qpRes.data) {
          const mappedQPs: QualityParameterState[] = qpRes.data.map((qp) => ({
            id: qp.id,
            name: qp.name,
            code: qp.code,
            type: qp.type,
            unit: qp.unit,
            minValue: qp.min_value !== null ? Number(qp.min_value) : null,
            maxValue: qp.max_value !== null ? Number(qp.max_value) : null,
          }));
          setQualityParameters(mappedQPs);
        }

        if (inspection?.id) {
          try {
            const inspData = await qcService.getInspectionById(inspection.id);
            if (active && inspData?.results) {
              setLoadedResults(inspData.results);
            }
          } catch (_) {}
        }
      } catch (err) {
        console.error('Failed to load checklists or quality parameters in ViewInspectionModal:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [inspection?.id]);

  const checklist = useMemo(() => {
    if (!inspection) return null;
    return checklists.find((c) => c.id === inspection.checklistId);
  }, [inspection, checklists]);

  const parameters = useMemo(() => {
    if (!checklist) return [];
    return (checklist.parameters || [])
      .map((pid) => qualityParameters.find((p) => p.id === pid))
      .filter(Boolean)
      .map((p) => p!);
  }, [checklist, qualityParameters]);

  const inspResults = useMemo(() => {
    const list = loadedResults.length > 0 ? loadedResults : results;
    return list.filter((r) => !inspection?.id || r.inspectionId === inspection.id);
  }, [loadedResults, results, inspection?.id]);

  if (!inspection) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Inspection Details — {inspection.inspectionNumber}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {inspection.itemName} {inspection.variantName ? `(${inspection.variantName})` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Source:</span>
            <p className="font-medium text-gray-900">{inspection.sourceNumber}</p>
          </div>
          <div>
            <span className="text-gray-500">Batch / Lot:</span>
            <p className="font-medium text-gray-900">
              {inspection.batchNumber || inspection.lotNumber
                ? `${inspection.batchNumber || ''} ${inspection.lotNumber || ''}`.trim()
                : '—'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Inspector:</span>
            <p className="font-medium text-gray-900">{inspection.inspectorName || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500">Completed:</span>
            <p className="font-medium text-gray-900">{inspection.completedDate || '—'}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Parameter Results</h3>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                inspection.status === 'PASSED'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {inspection.status}
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Parameter</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Observed Value</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parameters.length > 0 ? (
                  parameters.map((param) => {
                    const result = inspResults.find((r) => r.parameterId === param.id);
                    return (
                      <tr key={param.id}>
                        <td className="px-4 py-3 font-medium text-gray-900">{param.name}</td>
                        <td className="px-4 py-3 text-gray-600">{param.type.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {result?.observedValue || '—'}
                          {param.unit && result?.observedValue ? ` ${param.unit}` : ''}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {result ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                result.status === 'PASS'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : result.status === 'FAIL'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {result.status}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : inspResults.length > 0 ? (
                  inspResults.map((r, i) => (
                    <tr key={r.id || i}>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.parameterName || 'Quality Check'}</td>
                      <td className="px-4 py-3 text-gray-600">{r.parameterType?.replace('_', ' ') || 'PASS_FAIL'}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.observedValue || '—'}
                        {r.unit && r.observedValue ? ` ${r.unit}` : ''}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.status === 'PASS'
                              ? 'bg-emerald-100 text-emerald-700'
                              : r.status === 'FAIL'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                      Standard verification recorded (no individual parameters configured)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {inspection.notes && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Notes:</span> {inspection.notes}
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}